import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useReducedMotion} from 'framer-motion';
import useShow from '../../../hooks/useShow';
import {TEticketEvent, TEticketOrder, TEticketTicket} from '../../../redux/eTicket/eTicket.type';
import AdditionalInfoSheet from '../components/AdditionalInfoSheet/AdditionalInfoSheet';
import SeatMapSheet from '../components/SeatMapSheet/SeatMapSheet';
import ScheduleSheet from '../components/ScheduleSheet/ScheduleSheet';
import QrZoom from '../components/QrZoom/QrZoom';
import useQrZoomScan from '../components/QrZoom/hooks/useQrZoomScan';
import * as scanCss from '../components/QrZoom/QrZoomScanEffects.css';
import * as css from './TicketDetails.css';
import TicketDetailsBody from './ticketDetailsBody/TicketDetailsBody';
import TicketDetailsHeader from './ticketDetailsHeader/TicketDetailsHeader';
import TicketDetailsStub from './TicketDetailsStub';

interface TicketDetailsProps {
    ticket: TEticketTicket;
    index: number;
    total: number;
    event: TEticketEvent;
    order?: TEticketOrder;
}

function TicketDetails({ticket, index, total, event, order}: TicketDetailsProps) {
    const {token} = useParams();
    const qrShow = useShow();
    const infoShow = useShow();
    const seatMapShow = useShow();
    const scheduleShow = useShow();
    const reduceMotion = useReducedMotion();
    const containerRef = useRef<HTMLDivElement>(null);
    const cardQrRef = useRef<HTMLDivElement>(null);
    const zoomQrRef = useRef<HTMLDivElement>(null);
    const [isSlideActive, setIsSlideActive] = useState(false);
    const buyerName = ticket.inviterName ?? [order?.nameFirst, order?.nameLast].filter(Boolean).join(' ');
    const showMoreInfo = !!ticket.additionalInfo && ticket.additionalInfo.trim() !== '';
    const isSeated = !!ticket.seat && Object.values(ticket.seat).some(value => value != null && value !== '');
    // timetable events: the sessions booked on this ticket (the API lists them per ticket type)
    const showSchedule = !!ticket.sessions?.length;

    useEffect(() => {
        const slide = containerRef.current?.closest<HTMLElement>('[data-carousel-card]');
        const viewport = slide?.parentElement;
        if(!slide || !viewport) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            setIsSlideActive(entry.isIntersecting);
        }, {root: viewport, threshold: 0.6});

        observer.observe(slide);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const slide = containerRef.current?.closest<HTMLElement>('[data-carousel-card]');
        const viewport = slide?.parentElement;
        if(!slide || !viewport) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            setIsSlideActive(entry.isIntersecting);
        }, {root: viewport, threshold: 0.6});

        observer.observe(slide);
        return () => observer.disconnect();
    }, []);

    const resolveScanTarget = useCallback(() => {
        return (qrShow.isOpen ? zoomQrRef : cardQrRef).current;
    }, [qrShow.isOpen]);

    const {scanned, scanTs, isScanning, isPop} = useQrZoomScan({
        token,
        qid: ticket.qid,
        initialScanned: ticket.scanned,
        initialCanceled: ticket.canceled,
        initialScanTs: ticket.scanTs,
        reduceMotion,
        resolveScanTarget,
        pollEnabled: isSlideActive,
    });

    const showCardScanFx = isScanning && !qrShow.isOpen;
    const showZoomScanFx = isScanning && qrShow.isOpen;

    return (
        <css.container ref={containerRef} data-cy="ticket-details">
            {!reduceMotion && <scanCss.burstGlobalStyle />}
            <TicketDetailsHeader buyerName={buyerName} order={order} />
            <TicketDetailsBody
                event={event}
                index={index}
                ticket={{title: ticket.title, barcode: ticket.barcode, qid: ticket.qid, color: ticket.color, seat: ticket.seat, canceled: ticket.canceled}}
                total={total}
                cardQrRef={cardQrRef}
                isScanning={showCardScanFx}
                reduceMotion={reduceMotion}
                onQrZoom={qrShow.open}
            />
            {(showMoreInfo || isSeated || showSchedule) && (
                <TicketDetailsStub
                    onMoreInfo={showMoreInfo ? infoShow.open : undefined}
                    onSeatMap={isSeated ? seatMapShow.open : undefined}
                    onSchedule={showSchedule ? scheduleShow.open : undefined}
                />
            )}
            {isSeated && <SeatMapSheet show={seatMapShow} venue={event.location} seat={ticket.seat} design={event.seatMap} />}
            {showSchedule && <ScheduleSheet show={scheduleShow} sessions={ticket.sessions} event={event} />}
            {qrShow.isOpen && (
                <QrZoom
                    barcode={ticket.barcode}
                    qid={ticket.qid}
                    canceled={ticket.canceled}
                    scanned={scanned}
                    scanTs={scanTs}
                    isScanning={showZoomScanFx}
                    isPop={isPop}
                    zoomQrRef={zoomQrRef}
                    reduceMotion={reduceMotion}
                    ticketLabel={`${index + 1}/${total}`}
                    onClose={qrShow.close}
                />
            )}
            {(ticket.additionalInfo || ticket.customFields?.length > 0) && (
                <AdditionalInfoSheet additionalInfo={ticket.additionalInfo} customFields={ticket.customFields} show={infoShow} />
            )}
        </css.container>
    );
}

export default TicketDetails;
